import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';

import { logDevError } from './log-dev-error';
import type { NarrowExposureCohort, NarrowExposureConfig, NarrowExposureDecision } from './types';

const UNEVALUATED: NarrowExposureDecision = { outcome: 'unevaluated' };

/**
 * Reads the Statsig cohort for a narrow exposure experiment, which logs the exposure, so only call
 * it when the message is next in line to be shown. Once Statsig has initialised, `expVal` applies
 * Criterion overrides, which log no exposure.
 *
 * `expVal` returns the default for failed, uninitialised, unrecognised and unallocated
 * evaluations. Anything other than a string or boolean in exactly one cohort list is
 * `unevaluated`.
 */
export const evaluateCohort = (config: NarrowExposureConfig): NarrowExposureDecision => {
	const { statsigExperiment, statsigParameterName } = config;
	let cohort: unknown = null;

	try {
		cohort = expVal<unknown>(statsigExperiment, statsigParameterName, null);
	} catch {
		// The Statsig client swallows SDK errors; this covers a missing or replaced client.
	}

	if (typeof cohort !== 'string' && typeof cohort !== 'boolean') {
		return UNEVALUATED;
	}

	const showCohorts: ReadonlyArray<NarrowExposureCohort> = config.showCohorts;
	const dropCohorts: ReadonlyArray<NarrowExposureCohort> = config.dropCohorts;
	const inShow = showCohorts.includes(cohort);
	const inDrop = dropCohorts.includes(cohort);

	if (inShow && inDrop) {
		logDevError(
			`[Post Office] Narrow exposure experiment "${statsigExperiment}" has an invalid config: cohort "${String(cohort)}" is in both showCohorts and dropCohorts.`,
		);
		return UNEVALUATED;
	}

	if (!inShow && !inDrop) {
		logDevError(
			`[Post Office] Narrow exposure experiment "${statsigExperiment}" has an unrecognised cohort "${String(cohort)}" that is in neither showCohorts nor dropCohorts.`,
		);
		return UNEVALUATED;
	}

	return { outcome: inShow ? 'show' : 'drop', cohort };
};
